import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, ArrowDown } from "lucide-react";
import { profileData } from "@/data/profile";
const Aurora = React.lazy(() => import("./BackgroundAurora"));
const BlurText = React.lazy(() => import("./BlurText"));

export default function Hero() {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const rolesRef = useRef<HTMLDivElement>(null);
  const buttonsRef = useRef<HTMLDivElement>(null);
  const [device, setDevice] = useState("mobile");

  const updateDevice = () => {
    const width = window.innerWidth;
    if (width < 768) setDevice("mobile"); // <768px → Mobile
    else if (width < 1024) setDevice("tablet"); // 768–1023px → Tablet
    else setDevice("desktop"); // ≥1024px → Laptop/Desktop
  };

  useEffect(() => {
    if (typeof window === undefined) return;
    updateDevice();
    window.addEventListener("resize", updateDevice);
    return () => window.removeEventListener("resize", updateDevice);
  }, []);

  useEffect(() => {
  // guard SSR
  if (typeof window === "undefined") return;

  // skip animations for reduced motion users
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
    // set final states, optionally
    return;
  }

  // scope animations to the section/title using gsap.context (dynamically)
  let ctx: any = null;
  const run = async () => {
    if (typeof window === 'undefined') return;
    const gsapMod = await import('gsap');
    const gsapModule = (gsapMod.gsap || gsapMod.default || gsapMod) as any;

    ctx = gsapModule.context(() => {
      if (titleRef.current) {
        const chars = titleRef.current.textContent?.split("") || [];
        // mutate DOM (keeps your previous approach)
        titleRef.current.innerHTML = chars
          .map((char) => (char === " " ? "&nbsp;" : `<span class="inline-block">${char}</span>`))
          .join("");

        gsapModule.fromTo(
          Array.from(titleRef.current.children),
          { y: 100, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            stagger: 0.05,
            ease: "back.out(1.7)",
          }
        );
      }

      // Role chips
      if (rolesRef.current)
        gsapModule.fromTo(
          Array.from(rolesRef.current.children),
          { y: 50, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.6,
            stagger: 0.2,
            ease: "power2.out",
            delay: 0.5,
          }
        );

      if (buttonsRef.current)
        gsapModule.fromTo(
          Array.from(buttonsRef.current.children),
          { y: 50, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6, stagger: 0.2, ease: "power2.out", delay: 0.5 }
        );
    }, titleRef);

    return () => {
      if (ctx?.revert) ctx.revert();
    };
  };

  run();
}, []); 


  const scrollToProjects = () => {
    const element = document.getElementById("projects");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleAnimationComplete = () => {
    console.log("Animation Completed.");
  };

  const handleDownloadResume = () => {
    const link = document.createElement("a");
    link.href = "/Farhaan_Shaikh_Resume.pdf";
    link.download = "Farhaan_Shaikh_Resume.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      {device === "desktop" && (
        <Aurora
          colorStops={["#3A29FF", "#FF94B4", "#FF3232"]}
          blend={0.5}
          amplitude={1.0}
          speed={0.9}
        />
      )}

      <section
        id="hero"
        aria-label="Hero section"
        className="min-h-screen flex items-center justify-center relative overflow-hidden px-4"
      >
        <div className="text-center max-w-4xl mx-auto relative z-10">
          {/* Role chips */}
          <div
            ref={rolesRef}
            className="flex flex-wrap justify-center gap-4 mb-6"
          >
            {profileData.roles.map((role, index) => (
              <span
                key={role}
                className={`px-4 py-2 rounded-full text-sm font-medium border ${
                  index === 0
                    ? "tech-badge-primary"
                    : index === 1
                    ? "tech-badge-secondary"
                    : "tech-badge"
                }`}
              >
                {role}
              </span>
            ))}
          </div>

          {device === "desktop" ? (
            <BlurText
              text="Farhaan Shaikh"
              delay={100}
              animateBy="words"
              direction="top"
              onAnimationComplete={handleAnimationComplete}
              className="lg:text-8xl md:text-6xl text-6xl font-extrabold leading-16 justify-center tracking-tighter mb-2 bg-clip-text bg-white"
            />
          ) : (
            <>
              <BlurText
                text="Farhaan"
                delay={100}
                animateBy="words"
                direction="top"
                stepDuration={0.3}
                onAnimationComplete={handleAnimationComplete}
                className="lg:text-8xl md:text-6xl text-6xl text-white font-bold leading-16 justify-center tracking-tighter mb-2 bg-white bg-clip-text leading-none"
              />
              <BlurText
                text="Shaikh"
                delay={100}
                animateBy="words"
                stepDuration={0.6}
                direction="top"
                onAnimationComplete={handleAnimationComplete}
                className="text-center bg-gradient-to-r from-cyan-600 to-purple-400 bg-clip-text font-bold text-transparent tracking-tighter lg:text-7xl md:text-5xl text-5xl justify-center mb-4 leading-none"
              />{" "}
            </>
          )}

          <BlurText
            text="3+ years of turning coffee into code."
            delay={50}
            animateBy="words"
            direction="top"
            onAnimationComplete={handleAnimationComplete}
            className="md:text-xl text-lg max-w-3xl mx-auto text-center flex justify-center mb-12 bg-muted-foreground bg-clip-text"
          />

          {/* CTA Buttons */}
          <div
            ref={buttonsRef}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center md:w-auto w-4/5 mx-auto"
          >
            <Button
              onClick={scrollToProjects}
              size="lg"
              aria-label="View More"
              className="bg-primary text-white hover:bg-primary/90 font-medium px-8 py-6 text-lg rounded-xl w-full"
            >
              View Projects
              <ArrowDown className="ml-2 h-5 w-5" />
            </Button>

            <Button
              onClick={handleDownloadResume}
              //@ts-ignore
              variant="outline"
              size="lg"
              className="border-glass-border/50 bg-glass-bg/30 hover:bg-glass-bg font-medium px-8 py-6 text-lg rounded-xl text-white/80 hover:text-blue-500 w-full"
              aria-label="Download Resume"
            >
              Download Resume
              <Download className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
